import React, { createContext, useState, useEffect, useContext } from "react";

import { onBrowser } from "../helpers/onBrowser";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export interface ThemeContextType {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

/**
 * Exported so consumer apps can reference the storage contract from
 * pre-paint scripts and sync tests (e.g. constellation's index.html FOUC
 * script) instead of re-typing the literals.
 */
export const THEME_STORAGE_KEY = "confetti-theme";
export const THEME_ATTRIBUTE = "data-confetti-theme";
const TRANSITION_CLASS = "confetti-theme-transition";
const TRANSITION_DURATION = 500;

let isInitialApply = true;

function resolveSystemTheme(): ResolvedTheme {
  if (!onBrowser()) {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredPreference(): ThemePreference | null {
  if (!onBrowser()) {
    return null;
  }
  let stored: string | null;
  try {
    stored = localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (e.g., private browsing, blocked cookies).
    return null;
  }
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return null;
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") {
    return resolveSystemTheme();
  }
  return preference;
}

function applyThemeAttribute(resolved: ResolvedTheme): void {
  if (onBrowser()) {
    const el = document.documentElement;

    if (!isInitialApply) {
      el.classList.add(TRANSITION_CLASS);
      void el.offsetHeight; // force reflow so transitions are active before theme changes
    }

    el.setAttribute(THEME_ATTRIBUTE, resolved);

    if (!isInitialApply) {
      window.setTimeout(() => {
        el.classList.remove(TRANSITION_CLASS);
      }, TRANSITION_DURATION);
    }

    isInitialApply = false;
  }
}

const placeholderSetTheme = (): never => {
  throw new Error(
    "You tried to call setTheme outside of a ThemeProvider. Did you forget to wrap your app in <ThemeProvider>?"
  );
};

export function CreateOrUseExistingThemeContext(
  initial: ThemeContextType
): React.Context<ThemeContextType> {
  if (!onBrowser()) {
    return createContext<ThemeContextType>(initial);
  }

  if (!window.kazoo) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    window.kazoo = {} as any;
  }

  const existing = window.kazoo.themeContext;
  if (existing) {
    return existing;
  }

  window.kazoo.themeContext = createContext<ThemeContextType>(initial);
  return window.kazoo.themeContext;
}

const ThemeContext = CreateOrUseExistingThemeContext({
  theme: "light",
  resolvedTheme: "light",
  setTheme: placeholderSetTheme,
});

export function useTheme(): ThemeContextType {
  return useContext(ThemeContext);
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemePreference;
  /** When false, force light theme. Stored preference is preserved for later re-enable. */
  enabled?: boolean;
}

export function ThemeProvider({
  children,
  defaultTheme = "light",
  enabled = true,
}: ThemeProviderProps): JSX.Element {
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    const stored = getStoredPreference();
    return stored ?? defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    enabled ? resolveTheme(theme) : "light"
  );

  const setTheme = React.useCallback(
    (newTheme: ThemePreference) => {
      if (!enabled) {
        return;
      }
      setThemeState(newTheme);
      if (onBrowser()) {
        try {
          localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        } catch {
          // The preference just won't persist across sessions.
        }
      }
    },
    [enabled]
  );

  useEffect(() => {
    const resolved = enabled ? resolveTheme(theme) : "light";
    setResolvedTheme(resolved);
    applyThemeAttribute(resolved);
  }, [theme, enabled]);

  useEffect(() => {
    if (!enabled || theme !== "system" || !onBrowser()) {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent): void => {
      const resolved = e.matches ? "dark" : "light";
      setResolvedTheme(resolved);
      applyThemeAttribute(resolved);
    };

    mediaQuery.addEventListener("change", handler);
    return () => {
      mediaQuery.removeEventListener("change", handler);
    };
  }, [theme, enabled]);

  const contextValue = React.useMemo(
    () => ({
      theme: enabled ? theme : "light",
      resolvedTheme,
      setTheme,
    }),
    [theme, resolvedTheme, setTheme, enabled]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}
